'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import Script from 'next/script'

// FullStory only loads for the intersection of four gates, so the script is
// never fetched (zero CWV impact) for everyone else:
// 1. visitor has interacted with the page (first interaction — Lighthouse is
//    headless and never moves/scrolls/taps, so audits deterministically exclude
//    it; this interaction gate, NOT IntersectionObserver, is what keeps it out)
// 2. visitor is on an allowlisted page
// 3. visitor is in the US (fs_geo cookie, set by proxy.ts from x-vercel-ip-country)
// 4. visitor is in the sample cohort (sticky random roll, first eligible visit)
// Once loaded, recording continues for the rest of the SPA session even if the
// visitor navigates to a non-allowlisted page.
const FULLSTORY_PATHS = new Set(['/best/ai', '/best/email', '/best/social-media-management'])
const SAMPLE_RATE = 0.2
const SAMPLE_COOKIE = 'fs_sample'
const GEO_COOKIE = 'fs_geo'
const SAMPLE_COOKIE_MAX_AGE = 60 * 60 * 24 * 7

// Production + local dev only. Preview deployments are excluded so internal
// testing doesn't burn the limited FullStory session quota.
const FULLSTORY_ENABLED =
  process.env.NEXT_PUBLIC_VERCEL_ENV === 'production' || process.env.NODE_ENV === 'development'

// Events that count as "a real user is here". Mirrors the shared
// useDeferredActivation list so headless Lighthouse (which fires none of these)
// stays excluded.
const INTERACTION_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
  'mousemove',
] as const

function getCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`))
    ?.split('=')[1]
}

// Sticky sampling: the first eligible visit rolls the dice once, then the
// cookie keeps the visitor in/out of the cohort consistently for a week
function isInSampleCohort(): boolean {
  const existing = getCookie(SAMPLE_COOKIE)
  if (existing) return existing === '1'

  const inSample = Math.random() < SAMPLE_RATE ? '1' : '0'
  document.cookie = `${SAMPLE_COOKIE}=${inSample}; max-age=${SAMPLE_COOKIE_MAX_AGE}; path=/`
  return inSample === '1'
}

// FullStory-specific activation: flip true on the visitor's FIRST interaction.
//
// Intentionally NOT the shared useDeferredActivation hook. A recorder wants to
// start the moment real activity begins, and the interaction gate alone is what
// keeps headless Lighthouse out. The shared hook additionally waits for an idle
// window and an IntersectionObserver "visible" signal off a zero-size sentinel —
// which started recordings late and, because a 0x0 element's intersection is
// unreliable across browsers, occasionally never activated at all. None of that
// extra gating helps FullStory (IntersectionObserver would actually fire during
// a Lighthouse audit, since the page renders), so we drop it here while leaving
// useDeferredActivation untouched for every other consumer.
function useFirstInteraction(): boolean {
  const [activated, setActivated] = useState(false)

  useEffect(() => {
    if (activated) return

    const handleInteraction = () => {
      setActivated(true)
      INTERACTION_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, handleInteraction),
      )
    }

    INTERACTION_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, handleInteraction, { passive: true }),
    )

    return () =>
      INTERACTION_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, handleInteraction),
      )
  }, [activated])

  return activated
}

export default function FullStoryAnalytics() {
  const pathname = usePathname()
  const [enabled, setEnabled] = useState(false)
  const activated = useFirstInteraction()

  useEffect(() => {
    if (enabled || !activated) return
    if (!pathname || !FULLSTORY_PATHS.has(pathname)) return
    if (getCookie(GEO_COOKIE) !== '1') return
    if (!isInSampleCohort()) return
    setEnabled(true)
  }, [activated, pathname, enabled])

  const orgId = process.env.NEXT_PUBLIC_FULLSTORY_ORG_ID
  if (!orgId || !FULLSTORY_ENABLED) return null

  if (!enabled) return null

  return (
    <Script
      id="fullstory"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
window['_fs_host']='fullstory.com';
window['_fs_script']='edge.fullstory.com/s/fs.js';
window['_fs_org']='${orgId}';
window['_fs_namespace']='FS';
(function(m,n,e,t,l,o,g,y){if(e in m){if(m.console&&m.console.log){m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].')}return}g=m[e]=function(a,b,s){g.q?g.q.push([a,b,s]):g._api(a,b,s)};g.q=[];o=n.createElement(t);o.async=1;o.crossOrigin='anonymous';o.src='https://'+_fs_script;y=n.getElementsByTagName(t)[0];y.parentNode.insertBefore(o,y);g.identify=function(i,v,s){g(l,{uid:i},s);if(v)g(l,v,s)};g.setUserVars=function(v,s){g(l,v,s)};g.event=function(i,v,s){g('event',{n:i,p:v},s)};g.anonymize=function(){g.identify('{{false}}')};g.shutdown=function(){g('rec',!1)};g.restart=function(){g('rec',!0)};g.log=function(a,b){g('log',[a,b])};g.consent=function(a){g('consent',!(!a))};g.identifyAccount=function(i,v){o='account';v=v||{};v.acctId=i;g(o,v)};g.clearUserCookie=function(){};g.setVars=function(n,p){g('setVars',[n,p])};g._w={};y='XMLHttpRequest';g._w[y]=m[y];y='fetch';g._w[y]=m[y];if(m[y])m[y]=function(){return g._w[y].apply(this,arguments)};g._v="2.0.0"})(window,document,window['_fs_namespace'],'script','user');
`,
      }}
    />
  )
}
